import {ArchiveRecord} from 'archive/ArchiveConstants';
import {Permissions} from 'auth/PermissionConstants';
import {NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BioentityAliasInfo, TagSchema} from 'constants/RegistryConstants';
import {User} from 'constants/UserConstants';
import BaseFile, {BaseFileAttributes} from 'folder-items/BaseFile';
import {Tag, ThirdPartyStorageServiceData} from 'folder-items/FileConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import {SchemaValidationState} from 'schemas/SchemaValidationConstants';
import {Optional} from 'util/Types';

export interface BaseFolderItemAttributes extends BaseFileAttributes {
  name: string;
  displayLabel: Optional<string>;
  folder: NumberIDFolder | StringIDFolder | undefined;
  projectFolderId: Optional<string>;
  editURL: string;
  note: Optional<Note>;
  permissions: Permissions;
  modified_at: string;
  created_at: string;
  tags: Array<Tag>;
  authors: Array<User>;
  archiveRecord: ArchiveRecord | null;
  schemaValidationState: Optional<SchemaValidationState>;
  humanId: string;
  tagSchema: Optional<TagSchema>;
  thirdPartyStorageServiceData: ThirdPartyStorageServiceData;
  bioentityAliasInfos: Optional<Array<BioentityAliasInfo>>;
}

interface BaseFolderItem {
  get<K extends keyof BaseFolderItemAttributes>(key: K): BaseFolderItemAttributes[K];
  set<K extends keyof BaseFolderItemAttributes>(
    attributeName: K,
    value: BaseFolderItemAttributes[K],
    options?: Backbone.ModelSetOptions
  ): BaseFolderItem;
  set(obj: Partial<BaseFolderItemAttributes>, options?: Backbone.ModelSetOptions): BaseFolderItem;
}

class BaseFolderItem extends BaseFile {
  fileType(): ModelTypeConstants {
    // TODO: can delete, this overwrites an identical method
    return this.fileState().fileType();
  }
  name(): string {
    return this.get('name');
  }
  displayLabel(): Optional<string> {
    return this.get('displayLabel');
  }
  folder(): StringIDFolder | NumberIDFolder | undefined {
    return this.get('folder');
  }
  setFolder(folder: NumberIDFolder | StringIDFolder): void {
    this.set({folder});
  }
  projectFolderId(): Optional<string> {
    return this.get('projectFolderId');
  }
  syncProjectFolder(folder: StringIDFolder | NumberIDFolder): void {
    if (folder == null) {
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      return this.clearProjectFolder();
    }
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    return this.setProjectFolder(folder);
  }
  clearProjectFolder(): void {
    // @ts-expect-error: subclasses treat this as void; should remove return statement.
    return this.set({projectFolderId: null});
  }
  setProjectFolder(projectFolder: StringIDFolder | NumberIDFolder): void {
    // @ts-expect-error: subclasses treat this as void; should remove return statement.
    return this.set({projectFolderId: projectFolder.api_identifier});
  }
  editURL(): string {
    return this.get('editURL');
  }
  initialNote(): Optional<Note> {
    return this.get('note');
  }
  permissions(): Permissions {
    return this.get('permissions');
  }
  setPermissions(permissions: Permissions): void {
    this.set({permissions});
  }
  writable(): boolean {
    return this.permissions().writable;
  }
  allowEdits(): boolean {
    return this.writable();
  }
  allowEditsToNote(): boolean {
    return this.writable();
  } // by default, we assume note is versioned separately
  modifiedAt(): string {
    return this.get('modified_at');
  }
  createdAt(): string {
    return this.get('created_at');
  }
  tags(): Array<Tag> {
    return this.get('tags');
  }
  humanId(): string {
    return this.get('humanId');
  }
  archiveRecord(): ArchiveRecord | null {
    return this.get('archiveRecord');
  }
  setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    this.set({archiveRecord});
  }
  setSchemaValidationState(schemaValidationState: Optional<SchemaValidationState>): void {
    this.set({schemaValidationState});
  }
  isArchived(): boolean {
    return this.archiveRecord() != null;
  }
  authors(): Array<User> {
    return this.get('authors');
  }
  setTags(tags: Array<Tag>): void {
    this.set({tags});
  }
  setAuthors(authors: Array<User>): void {
    this.set({authors});
  }
  tagSchema(): Optional<TagSchema> {
    return this.get('tagSchema');
  }
  setTagSchema(tagSchema: TagSchema): void {
    this.set({tagSchema});
  }
  initialBioentityAliasInfos(): Optional<Array<BioentityAliasInfo>> {
    return this.get('bioentityAliasInfos');
  }
  thirdPartyStorageServiceData(): ThirdPartyStorageServiceData {
    return this.get('thirdPartyStorageServiceData');
  }
  setNameAndDisplayLabel(name: string, displayLabel: string): void {
    this.set({name, displayLabel});
  }
  schemaValidationState(): Optional<SchemaValidationState> {
    return this.get('schemaValidationState');
  }
  isRegistered(): boolean {
    // eslint-disable-next-line global-require
    const RegistryUtil = require('registry/RegistryUtil');
    return this.folder() != null && RegistryUtil.isRegistry(this.folder()!.source);
  }
  async triggerUpdateNameAction(name: string): Promise<string> {
    if (name !== this.name()) {
      // eslint-disable-next-line global-require
      const FileBrowserActions = require('file-browser/FileBrowserActions').default;
      await FileBrowserActions.renameFileWithDataMods(this.id, this.isRegistered(), {
        oldName: this.name(),
        newName: name,
        parentFolderId: this.folder()!.api_identifier,
      });
    }
    return this.name();
  }
}

export default BaseFolderItem;
